/**
 * **포털(방) 컬링** (2026-09-28) — 지금 카메라에서 **보이는 방**만 그린다.
 *
 * 왜: 그리는 것의 절반 이상이 벽 너머 다른 방의 가구였다(조합 홀에서 draw 1,257 중 약 800, 거리에서 송이경 집 안 280).
 * 거리 컬링(cullPieces 24m)과 시야 절두체는 벽 뒤를 못 거른다. draw 를 합치는 것(병합)은 이 맥북에서 전력 이득이 없었다 —
 * 안 보이는 것을 아예 안 그려야 CPU·GPU 가 같이 준다.
 *
 * 어떻게: 방이 칸이고 문·창이 칸 사이 구멍(포털)이다. 바깥도 칸 하나(OUT). 카메라가 있는 칸에서 시작해, 포털 사각형을 화면에
 * 투영해 지금 보이는 창(처음엔 화면 전체)과 겹치면 건너편 칸이 보인다 — 겹친 만큼으로 창을 좁혀 거기서 다시 들어간다(깊이 제한).
 * - 닫힌 불투명 문은 막는다. 유리문·창은 늘 통과(net 의 문 상태는 isOpen 으로 받는다).
 * - 포털이 카메라 평면에 걸치면(문턱에 서 있음) 지금 창을 그대로 넘긴다 — 틀리면 넉넉한 쪽으로.
 * - 방 밖인데 건물 안(벽 두께 속·방으로 안 적힌 틈)이면 그 건물 방 전부와 바깥을 보인다고 한다.
 * 레벨 데이터(방 사각형·문·창)만 쓰므로 다른 에피소드·다른 게임에도 그대로 붙는다.
 */
import * as THREE from 'three';

export const OUT = '(바깥)';

interface RoomRect { id: string; x1: number; z1: number; x2: number; z2: number; y?: number }
interface Opening { x: number; z: number; along: 'x' | 'z'; half?: number; y?: number }

interface Portal {
  a: string;
  b: string;
  corners: THREE.Vector3[];
  /** 이 문을 막는 문짝(소품 id) — 없으면 늘 통과 */
  door: string | null;
  glass: boolean;
}

export interface PortalWorld {
  /** 이 카메라에서 보이는 칸(방 id 와 OUT) */
  visible(cam: THREE.Camera): Set<string>;
  count: number;
}

export function buildPortals(o: {
  rooms: RoomRect[];
  doorways: Opening[];
  windows: Opening[];
  buildings: { x1: number; z1: number; x2: number; z2: number }[];
  wallH: number;
  roomAt: (x: number, z: number) => { id: string } | null;
  /** 문짝 소품 — 문 자리 가까이(1m) 있는 것을 그 문의 문짝으로 본다 */
  doors: { id: string; x: number; z: number; glass: boolean }[];
  isOpen: (doorId: string) => boolean;
}): PortalWorld {
  const roomY = new Map(o.rooms.map((r) => [r.id, r.y ?? 0]));
  const portals: Portal[] = [];
  const add = (w: Opening, kind: 'door' | 'window') => {
    const nx = w.along === 'x' ? 0 : 0.5, nz = w.along === 'x' ? 0.5 : 0;
    const a = o.roomAt(w.x + nx, w.z + nz)?.id ?? OUT, b = o.roomAt(w.x - nx, w.z - nz)?.id ?? OUT;
    if (a === b) return;
    // 개구부보다 조금 넉넉하게 — 층 높이 전체, 폭 +0.15
    const h = (w.half ?? (kind === 'door' ? 0.5 : 0.85)) + 0.15;
    const y0 = Math.min(roomY.get(a) ?? roomY.get(b) ?? 0, roomY.get(b) ?? roomY.get(a) ?? 0) - 0.1;
    const y1 = y0 + o.wallH + 0.3;
    const c = w.along === 'x'
      ? [new THREE.Vector3(w.x - h, y0, w.z), new THREE.Vector3(w.x + h, y0, w.z), new THREE.Vector3(w.x + h, y1, w.z), new THREE.Vector3(w.x - h, y1, w.z)]
      : [new THREE.Vector3(w.x, y0, w.z - h), new THREE.Vector3(w.x, y0, w.z + h), new THREE.Vector3(w.x, y1, w.z + h), new THREE.Vector3(w.x, y1, w.z - h)];
    let door: string | null = null, glass = kind === 'window';
    if (kind === 'door') {
      let best = 1.0;
      for (const d of o.doors) { const dd = Math.hypot(d.x - w.x, d.z - w.z); if (dd < best) { best = dd; door = d.id; glass = d.glass; } }
    }
    portals.push({ a, b, corners: c, door, glass });
  };
  for (const d of o.doorways) add(d, 'door');
  for (const w of o.windows) add(w, 'window');
  const adj = new Map<string, Portal[]>();
  for (const p of portals) for (const r of [p.a, p.b]) { const l = adj.get(r); if (l) l.push(p); else adj.set(r, [p]); }

  // 방이 어느 건물에 드는가 — 방 밖인데 건물 안일 때 쓴다
  const inRect = (x: number, z: number, r: { x1: number; z1: number; x2: number; z2: number }) =>
    x >= Math.min(r.x1, r.x2) && x <= Math.max(r.x1, r.x2) && z >= Math.min(r.z1, r.z2) && z <= Math.max(r.z1, r.z2);
  const roomsOf = o.buildings.map((b) => o.rooms.filter((r) => inRect((r.x1 + r.x2) / 2, (r.z1 + r.z2) / 2, b)).map((r) => r.id));

  const m = new THREE.Matrix4(), v = new THREE.Vector4();
  const FULL = [-1, -1, 1, 1];
  /** 포털을 화면에 투영한 사각형(NDC). 전부 뒤면 null, 카메라 평면에 걸치면 FULL */
  const rectOf = (p: Portal): number[] | null => {
    let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity, behind = 0;
    for (const c of p.corners) {
      v.set(c.x, c.y, c.z, 1).applyMatrix4(m);
      if (v.w <= 0.05) { behind++; continue; }
      const x = v.x / v.w, y = v.y / v.w;
      if (x < minx) minx = x; if (x > maxx) maxx = x; if (y < miny) miny = y; if (y > maxy) maxy = y;
    }
    if (behind === 4) return null;
    if (behind > 0) return FULL;
    return [minx, miny, maxx, maxy];
  };
  const MAX_DEPTH = 5;
  let visits = 0;

  const visible = (cam: THREE.Camera) => {
    cam.updateMatrixWorld();
    m.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    const pos = cam.getWorldPosition(new THREE.Vector3());
    const out = new Set<string>();
    let start = o.roomAt(pos.x, pos.z)?.id ?? null;
    if (!start) {
      const bi = o.buildings.findIndex((b) => inRect(pos.x, pos.z, b));
      if (bi >= 0) { for (const id of roomsOf[bi]) out.add(id); out.add(OUT); }   // 벽 두께 속 — 넉넉하게
      start = OUT;
    }
    out.add(start);
    visits = 0;
    const walk = (region: string, rect: number[], depth: number, from: Portal | null) => {
      for (const p of adj.get(region) ?? []) {
        if (p === from) continue;
        if (p.door && !p.glass && !o.isOpen(p.door)) continue;
        const r = rectOf(p);
        if (!r) continue;
        const x0 = Math.max(rect[0], r[0]), y0 = Math.max(rect[1], r[1]), x1 = Math.min(rect[2], r[2]), y1 = Math.min(rect[3], r[3]);
        if (x0 >= x1 || y0 >= y1) continue;
        const other = p.a === region ? p.b : p.a;
        out.add(other);
        if (depth < MAX_DEPTH && ++visits < 400) walk(other, [x0, y0, x1, y1], depth + 1, p);
      }
    };
    walk(start, FULL, 0, null);
    return out;
  };
  return { visible, count: portals.length };
}
